import { useRef, useState, type ReactNode } from "react";
import type {
  ProposalCollaborationEnvelope,
  ProposalNegotiationParty,
  ProposalPublicationSelection,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import type {
  ProposalChangeDescriptionContext,
  ProposalChangeDescriptor,
} from "work/proposals/negotiation/proposal-change-description";
import {
  describeProposalChangeLine,
  pairProposalDocumentReplacements,
} from "work/proposals/negotiation/proposal-change-descriptors";
import {
  proposalNegotiationRevisesWorkAgreement,
  proposalPublishableChanges,
  proposalSelectiveTranchePublicationApplies,
} from "work/proposals/negotiation/proposal-publishable-changes";
import { proposalSubmissionSummaryHeading } from "work/proposals/_views/_shared/proposal-submission-summary";
import {
  isInitialProposalSubmission,
  proposalSubmitAudienceLabel,
} from "work/proposals/_views/_shared/proposal-change-share-plan";
import {
  pendingProposalReviewWarning,
  proposalPendingReviewWarning,
} from "work/proposals/_views/_shared/proposal-pending-review-warning";
import type Individual from "marketplace/individuals/individual";
import PendingReviewerReminders from "work/proposals/reviewers/_views/pending-reviewer-reminders";
import SubmitProposalDialog, {
  type SubmitProposalDialogResult,
} from "work/proposals/_views/_shared/submit-proposal-dialog";

/**
 * The submission decision, asked once.
 *
 * Callers save first (so the change list is the authoritative post-save one), then call
 * `resolveSubmission` with the acknowledged envelope. The dialog names every change, says who receives
 * it, and offers explicit restoration to the prior submitted value — replacing the old
 * confirm-then-maybe-a-second-dialog sequence. Comments are not its subject: they are published as they
 * are posted.
 */

export const CANCELED_SUBMISSION = "canceledSubmission" as const;
export type CanceledSubmission = typeof CANCELED_SUBMISSION;

export type ResolvedSubmission =
  | CanceledSubmission
  | {
      revertSelection: readonly ProposalPublicationSelection[];
    };

export type SubmitProposalDialogRequest = {
  envelope: ProposalCollaborationEnvelope;
  title: string;
  confirmButtonText: string;
  context?: ProposalChangeDescriptionContext;
  /** Who leads the other party's team, as the directory knows them, when the page could resolve them. */
  recipient?: Individual;
  /**
   * The page's collaboration viewer. With it the warning names the pending reviewers and offers to
   * remind them (#6618); without it the warning stays a count.
   */
  viewerScope?: string;
};

/**
 * The same question for the one submission that has no envelope behind it yet: a draft nobody has
 * saved.
 *
 * Submit on such a draft used to create it, move the route to the saved draft, load its negotiation
 * and only then ask — so the answer came after the write it was supposed to authorize, and a Cancel
 * left a draft the user never agreed to create (#5666). Asked from what the editor already knows, the
 * question comes first and the save, the submission and the redirect all follow the one Yes.
 *
 * It carries no envelope because there is nothing to diff against: a first submission lists no changes
 * and offers no revert, which is exactly what `isInitialProposalSubmission` makes of a loaded envelope.
 */
export type UnsavedDraftSubmissionRequest = {
  title: string;
  confirmButtonText: string;
  /** The party the actor submits as. The audience is the other one. */
  viewingParty: ProposalNegotiationParty | undefined;
  /** The actor's own reviewers on the draft. None can have approved work that was never saved. */
  reviewerCount: number;
  /** @see SubmitProposalDialogRequest.recipient */
  recipient?: Individual;
};

export type SubmitProposalDialogController = {
  resolveSubmission: (request: SubmitProposalDialogRequest) => Promise<ResolvedSubmission>;
  resolveUnsavedDraftSubmission: (request: UnsavedDraftSubmissionRequest) => Promise<ResolvedSubmission>;
  submitProposalDialog: ReactNode;
};

type SubmitPrompt = {
  title: string;
  confirmButtonText: string;
  message?: string;
  pendingReviewers?: ReactNode;
  changes: ProposalChangeDescriptor[];
  audienceLabel: string;
  recipient?: Individual;
  changesHeading: string;
  allowRevert: boolean;
};

export default function useSubmitProposalDialog(): SubmitProposalDialogController {
  const [prompt, setPrompt] = useState<SubmitPrompt>();
  const resolverRef = useRef<((result: SubmitProposalDialogResult | undefined) => void) | undefined>(undefined);

  /** Draws the dialog and waits for the one answer it takes. Declared above its callers; see app/CLAUDE.md. */
  function ask(nextPrompt: SubmitPrompt): Promise<SubmitProposalDialogResult | undefined> {
    return new Promise<SubmitProposalDialogResult | undefined>((resolve) => {
      resolverRef.current = resolve;
      setPrompt(nextPrompt);
    });
  }

  async function resolveSubmission(request: SubmitProposalDialogRequest): Promise<ResolvedSubmission> {
    const { envelope, context } = request;
    // Selective publication only makes sense once something has already been published; a first
    // submission sends the whole proposal, so the list is informational rather than a choice.
    const allowRevert = proposalSelectiveTranchePublicationApplies(envelope);
    // The envelope's own line, not a stand-in built from the parts of it this dialog happened to need.
    // The stand-in dropped the one thing only the line carries — the value the counterparty can still
    // see — so this dialog showed a bare new value where the save confirmation and the field history
    // both drew the transition, which is the very divergence the shared change vocabulary exists to
    // prevent.
    //
    // A genuine first submission has nothing behind it to diff against, so the list would only
    // itemize every populated field as a "change" — noise the leader just typed themselves (#4952).
    // The dialog then shows the plain confirmation alone.
    //
    // A document replacement is two change lines — the add that carries `replacesId` and the removal
    // of the document it names — and the user did one thing, so the two are paired back into one row
    // by the shared rule every change surface describes through (#5549).
    const changes = isInitialProposalSubmission(envelope)
      ? []
      : pairProposalDocumentReplacements(
          proposalPublishableChanges(envelope).map((change) =>
            describeProposalChangeLine(envelope, change.line, context ?? {}),
          ),
        );

    // Derived here rather than asked of the caller: both submit paths name the same audience, and the
    // envelope already says which party is submitting.
    const audienceLabel = proposalSubmitAudienceLabel(envelope.viewingParty);

    const result = await ask({
      title: request.title,
      confirmButtonText: request.confirmButtonText,
      // From the acknowledged envelope rather than from the caller: both submit paths owe the leader
      // the same warning, and only that envelope knows which approvals survived the save that just ran.
      message: proposalPendingReviewWarning(envelope),
      // The same reviewers by name, each with a reminder. Submit stays unconditional (#5128): the
      // leader may remind and then submit, or cancel and wait.
      pendingReviewers: <PendingReviewerReminders envelope={envelope} viewerScope={request.viewerScope} />,
      changes,
      audienceLabel,
      recipient: request.recipient,
      // Two headings for the two lists still shown: a delta since the last submission, or a
      // revision's delta against the live work agreement. Meaningless (and unused, since `changes`
      // is empty) on a genuine first submission, which shows no list at all.
      changesHeading: proposalSubmissionSummaryHeading(
        allowRevert,
        proposalNegotiationRevisesWorkAgreement(envelope),
      ),
      allowRevert,
    });

    if (!result) return CANCELED_SUBMISSION;
    return {
      revertSelection: result.revertedChanges.flatMap(toPublicationSelections),
    };
  }

  async function resolveUnsavedDraftSubmission(request: UnsavedDraftSubmissionRequest): Promise<ResolvedSubmission> {
    const audienceLabel = proposalSubmitAudienceLabel(request.viewingParty);
    const result = await ask({
      title: request.title,
      confirmButtonText: request.confirmButtonText,
      message: pendingProposalReviewWarning(request.reviewerCount),
      // A draft that has never been saved has published nothing, so there is no prior value to draw a
      // change against and nothing to withhold. The dialog is the plain confirmation, which is what a
      // genuine first submission shows once its envelope exists too.
      changes: [],
      audienceLabel,
      recipient: request.recipient,
      changesHeading: proposalSubmissionSummaryHeading(false, false),
      allowRevert: false,
    });

    if (!result) return CANCELED_SUBMISSION;
    return { revertSelection: [] };
  }

  function closeDialog(result: SubmitProposalDialogResult | undefined) {
    setPrompt(undefined);
    resolverRef.current?.(result);
    resolverRef.current = undefined;
  }

  return {
    resolveSubmission,
    resolveUnsavedDraftSubmission,
    submitProposalDialog: prompt ? (
      <SubmitProposalDialog
        title={prompt.title}
        changes={prompt.changes}
        audienceLabel={prompt.audienceLabel}
        recipient={prompt.recipient}
        changesHeading={prompt.changesHeading}
        allowRevert={prompt.allowRevert}
        message={prompt.message}
        pendingReviewers={prompt.pendingReviewers}
        confirmButtonText={prompt.confirmButtonText}
        onSubmit={(result) => closeDialog(result)}
        onCancel={() => closeDialog(undefined)}
      />
    ) : null,
  };
}

/**
 * The wire shape is keyed by conflict key plus item; the descriptor's own `key` packs both, so it is
 * split back apart here rather than carrying a second identity through the dialog.
 *
 * One row can stand for more than one change. A replacement is drawn as a single row over its add
 * and its removal, and holding back half of it would send the counterparty a proposal with neither
 * document on it, so every key the row covers is reverted together.
 */
function toPublicationSelections(change: ProposalChangeDescriptor): ProposalPublicationSelection[] {
  return (change.coveredKeys ?? [change.key]).map((key) => {
    const [conflictKey, itemId] = key.split("|");
    return itemId ? { conflictKey, itemId } : { conflictKey };
  });
}
